// Tiny typed client over fetch.

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json()).error ?? ""; } catch { /* ignore */ }
    throw new Error(`${res.status} ${detail || res.statusText}`);
  }
  const ct = res.headers.get("content-type") ?? "";
  return ct.includes("json") ? (res.json() as Promise<T>) : (res.text() as unknown as T);
}

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";
export type BodyType = "none" | "json" | "text" | "xml" | "urlencoded" | "multipart";

export interface KV { key: string; value: string; enabled?: boolean }

export interface User {
  id: string;
  email: string;
  name: string | null;
  hasPassword: boolean;
  avatarUpdatedAt?: string | null;
  jsonTheme?: string;
  mfaEnabled?: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  owner_id: string;
  role: string;
  created_at: string;
  logo_updated_at?: string | null;
}

export interface Project {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  docs: string | null;
  icon: string | null;
  is_default: number;
  sort_order: number;
  // Headers added to every request in this project at run time. The
  // per-request headers still override (case-insensitive by name).
  default_headers: KV[];
  created_at: string;
  updated_at: string;
}

export interface Comment {
  id: string;
  parent_id: string | null;
  body: string;
  edited_at: string | null;
  created_at: string;
  author_id: string | null;
  author_name: string | null;
  author_email: string | null;
  author_avatar_updated_at: string | null;
}

export interface Collection {
  id: string;
  workspace_id: string;
  project_id: string | null;
  name: string;
  description: string | null;
  base_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface RequestSummary {
  id: string;
  collection_id: string;
  folder_id: string | null;
  name: string;
  method: string;
  url: string;
  sort_order: number;
  updated_at: string;
}

export interface RequestDetail extends RequestSummary {
  workspace_id: string;
  folder_id: string | null;
  protocol: "rest" | "graphql" | "grpc" | "ws";
  headers: KV[];
  query: KV[];
  body_type: BodyType;
  body: string | null;
  description: string | null;
  docs: string | null;
  pre_request_script: string | null;
  post_response_script: string | null;
  tests_script: string | null;
  auth: AuthSpec;
  current_revision_id: string | null;
  created_at: string;
}

export type AuthKind = "none" | "basic" | "bearer" | "oauth2" | "aws-sigv4" | "digest";
export interface AuthSpec {
  kind: AuthKind;
  fields: Record<string, string>;
}

export interface Revision {
  id: string;
  revision_no: number;
  name: string;
  method: string;
  url: string;
  note: string | null;
  // Computed on save by the server, e.g. "URL · Headers +1 ~2 · Body +12 chars".
  // Null for revisions written before this column existed.
  change_summary: string | null;
  created_at: string;
  author_id: string | null;
  author_email: string | null;
  author_name: string | null;
  author_avatar_updated_at: string | null;
}

export interface RevisionDetail extends Revision {
  headers: KV[];
  query: KV[];
  body_type: BodyType;
  body: string | null;
  description: string | null;
  created_by: string;
}

export interface RunSummary {
  id: string;
  status: number | null;
  method: string;
  url: string;
  content_type: string | null;
  duration_ms: number;
  response_size: number | null;
  error: string | null;
  ran_at: string;
  revision_id: string | null;
}

export interface RunDetail extends RunSummary {
  request_headers: Record<string, string>;
  request_body: string | null;
  response_headers: Record<string, string> | null;
  response_body: string | null;
}

export interface Environment {
  id: string;
  workspace_id: string;
  name: string;
  vars: Record<string, string>;
  color: string | null;
  is_default: boolean;
  // Personal envs are scoped to a single user — they never appear in
  // other workspace members' env lists, and the server refuses to
  // resolve their vars for anyone else at run time.
  is_personal: boolean;
  owner_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Webhook {
  id: string;
  collection_id: string;
  workspace_id: string;
  folder_id: string | null;
  name: string;
  token: string;
  url: string;                  // computed public URL
  description: string | null;
  docs: string | null;
  resp_status: number;
  resp_content_type: string;
  resp_body: string;
  forward_enabled: number;      // 0 | 1
  // Legacy single-target column — first entry of `forward_urls`.
  forward_url: string | null;
  // Up to 3 destinations. Always present (server synthesizes from
  // `forward_url` for old rows).
  forward_urls: string[];
  // Per-target config: URL + (optional) method + (optional) transform.
  // The server synthesizes this from the legacy shared columns when no
  // explicit targets are stored, so this is always populated when
  // forwarding is configured.
  forward_targets: WebhookForwardTarget[];
  forward_method: string | null;
  forward_transform: string | null;
  // Security: CIDR allowlist (null/[] = wide open) and optional bearer
  // token. The token itself is returned so the user can copy it from
  // the UI; `has_auth_token` is a convenience flag for list views.
  allowed_ips: string[];
  required_auth_token: string | null;
  has_auth_token: boolean;
  request_count: number;
  last_request_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface WebhookRequest {
  seq: number;
  id: string;
  method: string;
  path: string;
  query: [string, string][];
  headers: [string, string][];
  body: string | null;
  body_size: number;
  truncated: boolean;
  content_type: string | null;
  remote_ip: string | null;
  user_agent: string | null;
  host: string | null;
  received_at: string;
  // Legacy single-target columns — first entry of `forward_results`.
  forward_status: number | null;
  forward_headers: [string, string][] | null;
  forward_body: string | null;
  forward_duration_ms: number | null;
  forward_error: string | null;
  // Per-destination result array. One entry per configured forward URL.
  // Null when forwarding wasn't enabled for this capture.
  forward_results: WebhookForwardResult[] | null;
  transform_error: string | null;
  transformed_request: unknown | null;
}

export interface WebhookForwardTarget {
  url: string;
  method: string | null;
  transform: string | null;
}

export interface WebhookForwardResult {
  url: string | null;
  status: number | null;
  headers: [string, string][] | null;
  body: string | null;
  duration_ms: number | null;
  error: string | null;
}

// Sidebar-shaped summary used when listing webhooks in a collection.
// Same fields as a full Webhook but the route returns the whole row so
// we just reuse Webhook for the response type — keeping a typedef here
// for callers who only care about list metadata.
export interface WebhookSummary {
  id: string; name: string; token: string; url: string;
  folder_id: string | null;
  request_count: number; last_request_at: string | null;
  sort_order: number; created_at: string;
}

export interface WebhookRule {
  id: string; sort_order: number;
  method: string; path: string;
  resp_status: number;
  resp_headers: [string, string][];
  resp_body: string;
}

export type WebhookRuleInput = Omit<WebhookRule, "id" | "sort_order">;

export interface RunResult {
  runId: string;
  status: number | null;
  headers: Record<string, string> | null;
  body: string | null;
  size: number | null;
  contentType: string | null;
  durationMs: number;
  error: string | null;
  finalUrl: string;
  revisionId: string | null;
  tests?: TestResult[];
  scriptLog?: unknown[];
  // Echo of what we actually sent, after env-var substitution and
  // form/multipart encoding. Powers the "Request" tab in the response
  // pane so users can see exactly what went on the wire.
  request?: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body: string | null;
    bodyType: BodyType;
  };
}

export const api = {
  me: () => req<{ user: User | null }>("/api/me"),
  updateMe: (body: { name?: string; json_theme?: string }) =>
    req<{ ok: true }>("/api/me", { method: "PATCH", body: JSON.stringify(body) }),
  logout: () => req<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  register: (body: { email: string; password: string; name?: string; invite?: string }) =>
    req<{ ok: true; user: User }>("/api/auth/register", { method: "POST", body: JSON.stringify(body) }),
  login: (body: { email: string; password: string; invite?: string; mfaCode?: string }) =>
    req<{ ok: true; user: User }>("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
  forgotPassword: (email: string) =>
    req<{ ok: true }>("/api/auth/forgot", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) =>
    req<{ ok: true }>("/api/auth/reset", { method: "POST", body: JSON.stringify({ token, password }) }),
  updatePassword: (body: { currentPassword?: string; password: string }) =>
    req<{ ok: true }>("/api/auth/password", { method: "POST", body: JSON.stringify(body) }),
  myInvites: () =>
    req<{
      invites: Array<{
        id: string; email: string; token: string; expires_at: string; created_at: string;
        workspace_id: string; workspace_name: string; inviter_name: string | null; inviter_email: string;
      }>;
    }>("/api/invites/mine"),
  acceptInvite: (id: string) =>
    req<{ ok: true; workspaceId: string }>(`/api/invites/${id}/accept`, { method: "POST" }),
  rejectInvite: (id: string) =>
    req<{ ok: true }>(`/api/invites/${id}/reject`, { method: "POST" }),
  invite: {
    lookup: (token: string) =>
      req<{ id: string; email: string; expires_at: string; redeemed_at: string | null; workspace_name: string; inviter_name: string | null; inviter_email: string }>(`/api/invites/${token}`),
  },
  workspaces: {
    list: () => req<{ workspaces: Workspace[] }>("/api/workspaces"),
    create: (name: string) =>
      req<{ id: string; name: string }>("/api/workspaces", { method: "POST", body: JSON.stringify({ name }) }),
    rename: (id: string, name: string) =>
      req<{ ok: true; name: string }>(`/api/workspaces/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/workspaces/${id}`, { method: "DELETE" }),
    members: (id: string) =>
      req<{ members: Array<{ id: string; email: string; name: string | null; picture_url: string | null; avatar_updated_at: string | null; role: string; joined_at: string }> }>(`/api/workspaces/${id}/members`),
    invite: (id: string, email: string) =>
      req<{ id: string; email: string; url: string; emailSent: boolean; emailError: string | null }>(`/api/workspaces/${id}/invites`, { method: "POST", body: JSON.stringify({ email }) }),
    listInvites: (id: string) =>
      req<{ invites: Array<{ id: string; email: string; token: string; expires_at: string; redeemed_at: string | null; created_at: string }> }>(`/api/workspaces/${id}/invites`),
    cancelInvite: (id: string, inviteId: string) =>
      req<{ ok: true }>(`/api/workspaces/${id}/invites/${inviteId}`, { method: "DELETE" }),
    resendInvite: (id: string, inviteId: string) =>
      req<{ emailSent: boolean; emailError: string | null; url: string }>(`/api/workspaces/${id}/invites/${inviteId}/resend`, { method: "POST" }),
    removeMember: (id: string, uid: string) =>
      req<{ ok: true }>(`/api/workspaces/${id}/members/${uid}`, { method: "DELETE" }),
  },
  collections: {
    list: (workspaceId: string, projectId?: string | null) =>
      req<{ collections: Collection[] }>(`/api/collections?workspace=${workspaceId}${projectId ? `&project=${projectId}` : ""}`),
    get: (id: string) => req<{ collection: Collection & { project_id: string | null } }>(`/api/collections/${id}`),
    create: (body: { workspace_id: string; project_id?: string; name?: string; description?: string; base_url?: string }) =>
      req<{ id: string; name: string; project_id?: string }>(`/api/collections`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: { name?: string; description?: string | null; base_url?: string | null; project_id?: string; docs?: string | null }) =>
      req<{ ok: true }>(`/api/collections/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/collections/${id}`, { method: "DELETE" }),
    requests: (id: string) =>
      req<{ requests: RequestSummary[] }>(`/api/collections/${id}/requests`),
  },
  requests: {
    get: (id: string) => req<{ request: RequestDetail }>(`/api/requests/${id}`),
    create: (body: { collection_id: string; name?: string; method?: string; url?: string; headers?: KV[]; query?: KV[]; body_type?: BodyType; body?: string | null }) =>
      req<{ id: string; revisionId: string }>(`/api/requests`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: {
      name?: string;
      method?: string;
      url?: string;
      headers?: KV[];
      query?: KV[];
      body_type?: BodyType;
      body?: string | null;
      description?: string | null;
      docs?: string | null;
      pre_request_script?: string | null;
      post_response_script?: string | null;
      tests_script?: string | null;
      auth?: AuthSpec | null;
      protocol?: "rest" | "graphql" | "grpc" | "ws";
      folder_id?: string | null;
      note?: string;
    }) =>
      req<{ ok: true; revisionId: string; revisionNo: number }>(`/api/requests/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    move: (id: string, body: { folder_id?: string | null; sort_order?: number }) =>
      req<{ ok: true }>(`/api/requests/${id}/move`, { method: "POST", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/requests/${id}`, { method: "DELETE" }),
    revisions: (id: string) =>
      req<{ revisions: Revision[] }>(`/api/requests/${id}/revisions`),
    revision: (id: string, revId: string) =>
      req<{ revision: RevisionDetail }>(`/api/requests/${id}/revisions/${revId}`),
    restoreRevision: (id: string, revId: string) =>
      req<{ ok: true; revisionId: string; revisionNo: number }>(`/api/requests/${id}/revisions/${revId}/restore`, { method: "POST" }),
    run: (id: string, body: {
      overrides?: Partial<{ method: string; url: string; headers: KV[]; query: KV[]; body_type: BodyType; body: string | null }>;
      environmentId?: string;
      save?: boolean;
      executor?: "worker" | "agent";
      agentId?: string;
      collectionRunId?: string | null;
      testResults?: TestResult[];
      scriptLog?: unknown[];
    } = {}) =>
      req<RunResult & { executor?: "worker" | "agent"; agentId?: string | null }>(`/api/requests/${id}/run`, { method: "POST", body: JSON.stringify(body) }),
    runs: (id: string) =>
      req<{ runs: RunSummary[] }>(`/api/requests/${id}/runs`),
    run_detail: (id: string, runId: string) =>
      req<{ run: RunDetail }>(`/api/requests/${id}/runs/${runId}`),
  },
  environments: {
    list: (workspaceId: string) =>
      req<{ environments: Environment[] }>(`/api/environments?workspace=${workspaceId}`),
    create: (body: { workspace_id: string; name?: string; vars?: Record<string, string>; color?: string | null; is_default?: boolean; is_personal?: boolean }) =>
      req<{ id: string; name: string; is_personal: boolean }>(`/api/environments`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: { name?: string; vars?: Record<string, string>; color?: string | null; is_default?: boolean }) =>
      req<{ ok: true }>(`/api/environments/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/environments/${id}`, { method: "DELETE" }),
  },
  agents: {
    list: (workspaceId: string) =>
      req<{ agents: Array<{ id: string; name: string; last_seen_at: string | null; revoked_at: string | null; created_at: string; online: boolean }> }>(`/api/agents?workspace=${workspaceId}`),
    create: (workspace_id: string, name: string) =>
      req<{ id: string; name: string; token: string; wsUrl: string }>(`/api/agents`, { method: "POST", body: JSON.stringify({ workspace_id, name }) }),
    revoke: (id: string) =>
      req<{ ok: true }>(`/api/agents/${id}/revoke`, { method: "POST" }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/agents/${id}`, { method: "DELETE" }),
  },
  folders: {
    list: (collectionId: string) =>
      req<{ folders: Array<{ id: string; collection_id: string; parent_folder_id: string | null; name: string; docs: string | null; sort_order: number }> }>(`/api/folders?collection=${collectionId}`),
    create: (body: { collection_id: string; parent_folder_id?: string | null; name?: string }) =>
      req<{ id: string; name: string }>(`/api/folders`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: { name?: string; docs?: string | null; parent_folder_id?: string | null; sort_order?: number }) =>
      req<{ ok: true }>(`/api/folders/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/folders/${id}`, { method: "DELETE" }),
  },
  collectionRuns: {
    start: (body: { collection_id: string; folder_id?: string | null; env_id?: string | null; agent_id?: string | null; executor?: "worker" | "agent" }) =>
      req<{ id: string }>(`/api/collection-runs`, { method: "POST", body: JSON.stringify(body) }),
    addItem: (runId: string, body: { request_id: string; ordinal: number; status: "pass" | "fail" | "error" | "skip"; duration_ms: number; assertions: Array<{ name: string; status: string; message?: string }>; script_log?: unknown[]; request_run_id?: string | null }) =>
      req<{ ok: true }>(`/api/collection-runs/${runId}/items`, { method: "POST", body: JSON.stringify(body) }),
    finish: (runId: string, status: "completed" | "failed" | "cancelled" = "completed") =>
      req<{ ok: true; totals: { requests: number; passed: number; failed: number; errored: number; duration_ms: number } }>(`/api/collection-runs/${runId}/finish`, { method: "POST", body: JSON.stringify({ status }) }),
    list: (collectionId: string) =>
      req<{ runs: Array<{ id: string; status: string; executor: string; totals: string; ran_by: string; started_at: string; finished_at: string | null; env_id: string | null; agent_id: string | null }> }>(`/api/collection-runs?collection=${collectionId}`),
    get: (runId: string) =>
      req<{ run: Record<string, unknown>; items: Array<{ ordinal: number; status: string; duration_ms: number; assertions: string; request_id: string; request_name: string; method: string; url: string }> }>(`/api/collection-runs/${runId}`),
  },
  openapi: {
    get: (collectionId: string) =>
      req<{ spec: null | { id: string; source_url: string | null; spec_hash: string; spec_version: string | null; last_synced_at: string; last_drift_at: string | null; last_drift: { added: unknown[]; removed: unknown[]; changed: unknown[] } | null } }>(`/api/openapi?collection=${collectionId}`),
    import: (body: { collection_id: string; source_url?: string; spec_text?: string; create_missing?: boolean }) =>
      req<{ id: string; summary: { endpoints: number; added: number; removed: number; changed: number }; drift: { added: unknown[]; removed: unknown[]; changed: unknown[] } }>(`/api/openapi/import`, { method: "POST", body: JSON.stringify(body) }),
  },
  secrets: {
    list: (workspaceId: string) =>
      req<{ providers: Array<{ id: string; name: string; kind: string; created_at: string; updated_at: string }> }>(`/api/secrets?workspace=${workspaceId}`),
    create: (body: { workspace_id: string; name: string; kind: "vault" | "aws_sm" | "azure_kv"; config: Record<string, string> }) =>
      req<{ id: string; name: string; kind: string }>(`/api/secrets`, { method: "POST", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/secrets/${id}`, { method: "DELETE" }),
  },
  projects: {
    list: (workspaceId: string) =>
      req<{ projects: Project[] }>(`/api/projects?workspace=${workspaceId}`),
    create: (body: { workspace_id: string; name?: string; icon?: string; description?: string }) =>
      req<{ id: string; name: string }>(`/api/projects`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: { name?: string; icon?: string | null; description?: string | null; docs?: string | null; sort_order?: number; default_headers?: KV[] }) =>
      req<{ ok: true }>(`/api/projects/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/projects/${id}`, { method: "DELETE" }),
  },
  avatars: {
    uploadMe: async (file: File) => {
      const res = await fetch(`/api/avatars/me`, { method: "POST", credentials: "include", headers: { "Content-Type": file.type }, body: file });
      if (!res.ok) { let d = ""; try { d = (await res.json()).error ?? ""; } catch {} throw new Error(`${res.status} ${d || res.statusText}`); }
      return (await res.json()) as { ok: true; updatedAt: string };
    },
    removeMe: () => req<{ ok: true }>(`/api/avatars/me`, { method: "DELETE" }),
    userUrl: (userId: string, version?: string | null) =>
      `/api/avatars/users/${userId}${version ? `?v=${encodeURIComponent(version)}` : ""}`,
    uploadWorkspace: async (workspaceId: string, file: File) => {
      const res = await fetch(`/api/avatars/workspaces/${workspaceId}`, { method: "POST", credentials: "include", headers: { "Content-Type": file.type }, body: file });
      if (!res.ok) { let d = ""; try { d = (await res.json()).error ?? ""; } catch {} throw new Error(`${res.status} ${d || res.statusText}`); }
      return (await res.json()) as { ok: true; updatedAt: string };
    },
    removeWorkspace: (workspaceId: string) =>
      req<{ ok: true }>(`/api/avatars/workspaces/${workspaceId}`, { method: "DELETE" }),
    workspaceUrl: (workspaceId: string, version?: string | null) =>
      `/api/avatars/workspaces/${workspaceId}${version ? `?v=${encodeURIComponent(version)}` : ""}`,
  },
  comments: {
    list: (target_kind: "request" | "collection" | "folder" | "project" | "workspace", target_id: string) =>
      req<{ comments: Comment[] }>(`/api/comments?target_kind=${target_kind}&target_id=${encodeURIComponent(target_id)}`),
    create: (body: { target_kind: "request" | "collection" | "folder" | "project" | "workspace"; target_id: string; body: string; parent_id?: string | null }) =>
      req<{ id: string }>(`/api/comments`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: string) =>
      req<{ ok: true }>(`/api/comments/${id}`, { method: "PATCH", body: JSON.stringify({ body }) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/comments/${id}`, { method: "DELETE" }),
  },
  mfa: {
    status: () => req<{ enrolled: boolean; enabled: boolean; enrolledAt: string | null }>("/api/mfa/me"),
    start:  () => req<{ secret: string; otpauth: string }>("/api/mfa/start", { method: "POST" }),
    confirm: (code: string) => req<{ ok: true; recoveryCodes: string[] }>("/api/mfa/confirm", { method: "POST", body: JSON.stringify({ code }) }),
    disable: (code: string) => req<{ ok: true }>("/api/mfa/disable", { method: "POST", body: JSON.stringify({ code }) }),
  },
  webhooks: {
    list: (workspaceId: string) =>
      req<{ webhooks: Webhook[] }>(`/api/webhooks?workspace=${workspaceId}`),
    listByCollection: (collectionId: string) =>
      req<{ webhooks: Webhook[] }>(`/api/webhooks/collection/${collectionId}`),
    get: (id: string) => req<{ webhook: Webhook }>(`/api/webhooks/${id}`),
    create: (body: { collection_id: string; name?: string; folder_id?: string | null }) =>
      req<{ id: string; name: string; token: string; url: string }>(`/api/webhooks`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: {
      name?: string;
      description?: string | null;
      docs?: string | null;
      resp_status?: number;
      resp_content_type?: string;
      resp_body?: string;
      forward_enabled?: boolean | number;
      forward_url?: string | null;
      forward_urls?: string[] | null;
      forward_targets?: WebhookForwardTarget[] | null;
      forward_method?: string | null;
      forward_transform?: string | null;
      allowed_ips?: string[] | null;
      required_auth_token?: string | null;
      folder_id?: string | null;
    }) =>
      req<{ ok: true }>(`/api/webhooks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/webhooks/${id}`, { method: "DELETE" }),
    move: (id: string, body: { folder_id?: string | null; sort_order?: number }) =>
      req<{ ok: true }>(`/api/webhooks/${id}/move`, { method: "POST", body: JSON.stringify(body) }),
    requests: (id: string, after?: number) =>
      req<{ requests: WebhookRequest[] }>(`/api/webhooks/${id}/requests${after !== undefined ? `?after=${after}` : ""}`),
    clearRequests: (id: string) =>
      req<{ ok: true }>(`/api/webhooks/${id}/requests`, { method: "DELETE" }),
    deleteRequest: (reqId: string) =>
      req<{ ok: true }>(`/api/webhooks/requests/${reqId}`, { method: "DELETE" }),
    rules: (id: string) => req<{ rules: WebhookRule[] }>(`/api/webhooks/${id}/rules`),
    addRule: (id: string, rule: WebhookRuleInput) =>
      req<{ id: string }>(`/api/webhooks/${id}/rules`, { method: "POST", body: JSON.stringify(rule) }),
    updateRule: (id: string, ruleId: string, rule: WebhookRuleInput) =>
      req<{ ok: true }>(`/api/webhooks/${id}/rules/${ruleId}`, { method: "PATCH", body: JSON.stringify(rule) }),
    deleteRule: (id: string, ruleId: string) =>
      req<{ ok: true }>(`/api/webhooks/${id}/rules/${ruleId}`, { method: "DELETE" }),
    reorderRules: (id: string, ids: string[]) =>
      req<{ ok: true }>(`/api/webhooks/${id}/rules/order`, { method: "PUT", body: JSON.stringify({ ids }) }),
  },
  flows: {
    list: (workspaceId: string) =>
      req<{ flows: Flow[] }>(`/api/flows?workspace=${workspaceId}`),
    listByCollection: (collectionId: string) =>
      req<{ flows: Flow[] }>(`/api/flows/collection/${collectionId}`),
    get: (id: string) => req<{ flow: Flow }>(`/api/flows/${id}`),
    create: (body: { collection_id: string; name?: string; folder_id?: string | null; steps?: FlowStep[] }) =>
      req<{ id: string; name: string; revisionId: string }>(`/api/flows`, { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: {
      name?: string;
      description?: string | null;
      docs?: string | null;
      steps?: FlowStep[];
      folder_id?: string | null;
      note?: string;
    }) => req<{ ok: true; revisionId: string | null }>(`/api/flows/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: true }>(`/api/flows/${id}`, { method: "DELETE" }),
    move: (id: string, body: { folder_id?: string | null; sort_order?: number }) =>
      req<{ ok: true }>(`/api/flows/${id}/move`, { method: "POST", body: JSON.stringify(body) }),
    run: (id: string, body: { environmentId?: string; input?: unknown; executor?: "worker" | "agent"; agentId?: string } = {}) =>
      req<{ run: FlowRun; steps: FlowRunStep[] }>(`/api/flows/${id}/run`, { method: "POST", body: JSON.stringify(body) }),
    runs: (id: string, limit?: number) =>
      req<{ runs: FlowRun[] }>(`/api/flows/${id}/runs${limit ? `?limit=${limit}` : ""}`),
    runDetail: (id: string, runId: string) =>
      req<{ run: FlowRun; steps: FlowRunStep[] }>(`/api/flows/${id}/runs/${runId}`),
    deleteRun: (id: string, runId: string) =>
      req<{ ok: true }>(`/api/flows/${id}/runs/${runId}`, { method: "DELETE" }),
    revisions: (id: string) =>
      req<{ revisions: FlowRevision[] }>(`/api/flows/${id}/revisions`),
    restoreRevision: (id: string, revId: string) =>
      req<{ ok: true; revisionId: string; revisionNo: number }>(`/api/flows/${id}/revisions/${revId}/restore`, { method: "POST" }),
  },
  imports: {
    run: (body: { collection_id: string; format: "postman" | "insomnia" | "bruno"; payload: unknown }) =>
      req<{ created: number; folders: number; warnings: string[] }>(`/api/imports`, { method: "POST", body: JSON.stringify(body) }),
  },
};

export interface TestResult { name: string; status: "pass" | "fail" | "skip"; message?: string; durationMs: number }

// ── Flows ─────────────────────────────────────────────────────────────

// Discriminated union: a flow's spec is an ordered list of step objects.
// The shape is intentionally permissive so a future server can introduce
// new step kinds without breaking older clients (the executor's
// "unknown_step_kind" path still records a row).
export type FlowStep =
  | {
      id: string;
      kind: "request";
      name: string;
      disabled?: boolean;
      source: "saved" | "inline";
      requestId?: string;
      method?: string;
      url?: string;
      headers?: KV[];
      query?: KV[];
      body?: string;
      body_type?: BodyType;
      overrides?: { method?: string; url?: string; headers?: KV[]; query?: KV[]; body?: string; body_type?: BodyType };
      extract?: Array<{ as: string; from: string }>;
      expect?: { status?: string };
      onFailure?: "stop" | "continue";
    }
  | { id: string; kind: "script"; name: string; disabled?: boolean; source: string }
  | { id: string; kind: "delay"; name: string; disabled?: boolean; ms: number };

export interface Flow {
  id: string;
  collection_id: string;
  workspace_id: string;
  folder_id: string | null;
  name: string;
  description: string | null;
  docs: string | null;
  steps: FlowStep[];
  sort_order: number;
  current_revision_id: string | null;
  created_at: string;
  updated_at: string;
}

// Lightweight shape used in sidebar lists. The server returns the full
// Flow row for both list endpoints — this typedef is here for callers
// that only care about the sidebar columns.
export interface FlowSummary {
  id: string;
  name: string;
  folder_id: string | null;
  sort_order: number;
  created_at: string;
}

export interface FlowRun {
  id: string;
  flow_id: string;
  workspace_id: string;
  status: "running" | "succeeded" | "failed" | "cancelled";
  environment_id: string | null;
  executor: string;
  agent_id: string | null;
  started_by: string | null;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  total_steps: number;
  failed_step_index: number | null;
  error: string | null;
  final_context?: unknown;
}

export interface FlowRunStep {
  id: string;
  step_index: number;
  step_id: string;
  step_kind: string;
  step_name: string;
  status: "pending" | "running" | "succeeded" | "failed" | "skipped";
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  input: unknown;
  output: unknown;
  error: string | null;
  request_run_id: string | null;
}

export interface FlowRevision {
  id: string;
  revision_no: number;
  name: string;
  description: string | null;
  note: string | null;
  created_at: string;
  author_id: string | null;
  author_email: string | null;
  author_name: string | null;
  author_avatar_updated_at: string | null;
}

